// panel/src/examples/Navbars/DashboardNavbar/CallBar.js
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useState } from "react";
import { useVoice } from "context/VoiceContext";

// Top-right over the navbar on wide screens. On phones it's a full-width strip: a live call sits
// at the top (at the bottom it covered the chat composer and Send button), while the dismissible
// "unavailable" notice sits at the bottom so it doesn't hide the menu button.
const barPosition = (edge) => ({
  position: "fixed",
  zIndex: 2000,
  top: { xs: edge === "top" ? "calc(8px + env(safe-area-inset-top, 0px))" : "auto", md: 12 },
  bottom: {
    xs: edge === "bottom" ? "calc(8px + env(safe-area-inset-bottom, 0px))" : "auto",
    md: "auto",
  },
  left: { xs: 8, md: "auto" },
  right: { xs: 8, md: 24 },
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 1.5,
  px: 2,
  py: 1,
});

function CallBar() {
  const voice = useVoice();
  const [errorDismissed, setErrorDismissed] = useState(false);
  if (!voice || voice.status === "unavailable") return null;

  const { status, incomingCall, acceptCall, rejectCall, hangUp, toggleMute, muted } = voice;

  if (status === "error") {
    if (errorDismissed) return null;
    return (
      <Card sx={{ ...barPosition("bottom"), backgroundColor: "error.main" }}>
        <Icon sx={{ color: "#fff" }}>error_outline</Icon>
        <MDTypography variant="button" sx={{ color: "#fff", flex: 1 }}>
          Voice calling unavailable
        </MDTypography>
        <IconButton
          size="small"
          onClick={() => setErrorDismissed(true)}
          sx={{ color: "#fff" }}
          aria-label="Dismiss"
        >
          <Icon>close</Icon>
        </IconButton>
      </Card>
    );
  }

  if (status === "idle") return null;

  const label =
    status === "ringing-in"
      ? `Incoming call — ${incomingCall?.parameters?.From || "unknown"}`
      : status === "ringing-out"
      ? "Calling…"
      : "On call";

  return (
    <Card
      sx={{
        ...barPosition("top"),
        backgroundColor: status === "ringing-in" ? "success.main" : "info.main",
      }}
    >
      <Icon sx={{ color: "#fff" }}>phone_in_talk</Icon>
      <MDTypography variant="button" sx={{ color: "#fff" }}>
        {label}
      </MDTypography>

      {status === "ringing-in" && (
        <>
          <MDButton size="small" color="white" onClick={acceptCall}>
            Accept
          </MDButton>
          <MDButton size="small" color="white" variant="outlined" onClick={rejectCall}>
            Decline
          </MDButton>
        </>
      )}

      {(status === "in-call" || status === "ringing-out") && (
        <>
          {status === "in-call" && (
            <IconButton size="small" onClick={toggleMute} sx={{ color: "#fff" }}>
              <Icon>{muted ? "mic_off" : "mic"}</Icon>
            </IconButton>
          )}
          <IconButton size="small" onClick={hangUp} sx={{ color: "#fff" }}>
            <Icon>call_end</Icon>
          </IconButton>
        </>
      )}
    </Card>
  );
}

export default CallBar;
